// The compose session as a pure reducer over `@bindery/composition`: the
// composition itself (every change through `applyEdit`, so the log replays),
// the recordings the child's words came from (browser memory, this session
// only), the cursor the next dictation lands at, and which word is open on
// the ladder. Ids and instants are minted by the handlers and passed in.
import {
  applyEdit,
  type Composition,
  type CompositionEdit,
  type CompositionToken,
  type DescribeResult,
  type EditError,
  type IntentJudgement,
  type LadderRung,
  PROVISIONAL_THRESHOLDS,
  recordingsOf,
  type SightMap,
  sightOf,
  sentences,
  spanReplacement,
  type SpanReplacement,
  surfaced,
  type SurfacingContext,
  type SurfacingThresholds,
  tokenLineage,
  type WordToken,
  writtenAt,
} from "@bindery/composition";
import type { Provenance } from "@bindery/speech/contract";
import { assertNever } from "@bindery/shared/std/assert-never";
import { failedTranscriptionsOf } from "./ladder";

export interface Refusal {
  readonly edit: CompositionEdit;
  readonly error: EditError;
}

export interface ComposeSession {
  readonly composition: Composition;
  /** dictationId → the recording its words were cut from; never persisted. */
  readonly clips: ReadonlyMap<string, Blob>;
  /** Sentence index the next dictation lands before; the count means the end. */
  readonly cursor: number;
  /**
   * The last word of a run of typed words being built, so the next typed
   * word and the dictation that follows land after it; null between runs,
   * when the cursor decides. An id, not an index: a deleted or re-said
   * sentence shifts indices and would otherwise land words in the wrong
   * sentence or outside the text.
   */
  readonly typingAfter: string | null;
  /** The word whose ladder is open. */
  readonly focus: string | null;
  readonly sight: SightMap;
  readonly thresholds: SurfacingThresholds;
  /**
   * Lower-cased word text → whether it is on the server's word list, as the
   * word-intent route answered (the list is server-only). A typed, chosen or
   * described word is a dictionary word by construction and is entered here
   * as it lands.
   */
  readonly dictionary: ReadonlyMap<string, boolean>;
  /**
   * Dictated words whose dictionary answer has not come back yet. Intent
   * markers arrive after the sentence (plan § 4, point 8), so these are not
   * doubted for the dictionary until the route answers or gives up; then
   * `surfaced`'s own rule for a missing signal applies.
   */
  readonly awaitingDictionary: ReadonlySet<string>;
  /** Edits `applyEdit` refused, for the lab panel; the child never sees one. */
  readonly refusals: readonly Refusal[];
  /** Ids `judge` edits named that were no longer words when they landed. */
  readonly skippedJudgements: readonly string[];
}

interface Landed {
  readonly tokens: readonly CompositionToken[];
  readonly asr: Provenance;
  readonly dictationId: string;
  readonly clip: Blob | null;
  readonly at: number;
}

export type ComposeAction =
  | ({ readonly type: "dictation" } & Landed)
  | ({ readonly type: "re-say"; readonly spanIds: readonly string[] } & Landed)
  | {
      readonly type: "re-say-no-speech";
      readonly spanIds: readonly string[];
      readonly at: number;
    }
  | { readonly type: "confirm"; readonly tokenId: string; readonly at: number }
  | {
      readonly type: "reject";
      readonly tokenId: string;
      readonly rung: Extract<
        LadderRung,
        "hear-and-compare" | "choose" | "type"
      >;
      readonly at: number;
    }
  | {
      readonly type: "choose";
      readonly tokenId: string;
      readonly from: string;
      readonly to: string;
      readonly at: number;
    }
  | {
      readonly type: "type";
      readonly tokenId: string;
      readonly from: string;
      readonly to: string;
      readonly at: number;
    }
  | {
      readonly type: "save-for-adult";
      readonly tokenId: string;
      readonly at: number;
    }
  | {
      readonly type: "delete-sentence";
      readonly sentence: number;
      readonly at: number;
    }
  | {
      readonly type: "move-sentence";
      readonly from: number;
      readonly to: number;
      readonly at: number;
    }
  | {
      readonly type: "insert-typed";
      readonly tokenId: string;
      readonly text: string;
      readonly at: number;
    }
  | { readonly type: "cursor"; readonly sentence: number }
  | { readonly type: "focus"; readonly tokenId: string | null }
  | {
      readonly type: "judge";
      readonly judgements: readonly {
        readonly tokenId: string;
        readonly judgement: IntentJudgement;
      }[];
      readonly at: number;
    }
  | {
      readonly type: "describe";
      readonly tokenId: string;
      readonly failedTranscriptions: readonly string[];
      readonly result: DescribeResult;
      readonly at: number;
    }
  | {
      readonly type: "surface";
      readonly tokenIds: readonly string[];
      readonly at: number;
    }
  | {
      readonly type: "dictionary";
      /** Token id → dictionary word, as the route answered. */
      readonly entries: ReadonlyMap<string, boolean>;
      readonly at: number;
    }
  | {
      /** The route gave no dictionary answer for these words and will not. */
      readonly type: "dictionary-unknown";
      readonly tokenIds: readonly string[];
      readonly at: number;
    };

export const newSession = (input: {
  readonly id: string;
  readonly readerId: string;
  readonly sight: SightMap;
  readonly createdAt: number;
  readonly thresholds?: SurfacingThresholds;
}): ComposeSession => ({
  composition: {
    id: input.id,
    readerId: input.readerId,
    title: null,
    tokens: [],
    edits: [],
    phase: "creating",
    share: { state: "private" },
    createdAt: input.createdAt,
    finishedAt: null,
  },
  clips: new Map(),
  cursor: 0,
  typingAfter: null,
  focus: null,
  sight: input.sight,
  thresholds: input.thresholds ?? PROVISIONAL_THRESHOLDS,
  dictionary: new Map(),
  awaitingDictionary: new Set(),
  refusals: [],
  skippedJudgements: [],
});

const commit = (
  session: ComposeSession,
  edit: CompositionEdit,
): ComposeSession => {
  const result = applyEdit(session.composition, edit);
  return result.tag === "ok"
    ? {
        ...session,
        composition: result.value,
        skippedJudgements:
          result.skipped === undefined || result.skipped.length === 0
            ? session.skippedJudgements
            : [...session.skippedJudgements, ...result.skipped],
      }
    : {
        ...session,
        refusals: [...session.refusals, { edit, error: result.error }],
      };
};

const tokenIndexOf = (
  tokens: readonly CompositionToken[],
  id: string,
): number => tokens.findIndex((t) => t.id === id);

/** Where in the token array the sentence at `cursor` begins; the end past the last. */
export const insertIndexAt = (
  tokens: readonly CompositionToken[],
  cursor: number,
): number => {
  const first = sentences(tokens)[cursor]?.[0];
  return first === undefined ? tokens.length : tokenIndexOf(tokens, first);
};

/** Where the next typed or dictated tokens go: after the typed run, else at the cursor. */
export const insertIndexFor = (session: ComposeSession): number => {
  const tokens = session.composition.tokens;
  const after =
    session.typingAfter === null
      ? -1
      : tokenIndexOf(tokens, session.typingAfter);
  return after < 0 ? insertIndexAt(tokens, session.cursor) : after + 1;
};

/** A chosen or typed word as the story writes it in place of `tokenId`. */
const writtenFor = (
  session: ComposeSession,
  tokenId: string,
  word: string,
): string => {
  const tokens = session.composition.tokens;
  const index = tokenIndexOf(tokens, tokenId);
  return index < 0 ? word : writtenAt(tokens, index, word);
};

const sentenceOf = (
  tokens: readonly CompositionToken[],
  tokenId: string,
): readonly string[] | undefined =>
  sentences(tokens).find((group) => group.includes(tokenId));

const withClip = (session: ComposeSession, landed: Landed): ComposeSession =>
  landed.clip === null
    ? session
    : {
        ...session,
        clips: new Map([...session.clips, [landed.dictationId, landed.clip]]),
      };

/** Drops the recordings no remaining token was cut from; audio outlives its words by nothing. */
const pruneClips = (session: ComposeSession): ComposeSession => {
  const referenced = new Set(
    session.composition.tokens.flatMap((t) =>
      t.kind !== "punct" && t.asr !== null ? [t.asr.dictationId] : [],
    ),
  );
  const kept = [...session.clips].filter(([id]) => referenced.has(id));
  return kept.length === session.clips.size
    ? session
    : { ...session, clips: new Map(kept) };
};

const textOf = (session: ComposeSession, tokenId: string): string | null => {
  const token = session.composition.tokens.find((t) => t.id === tokenId);
  return token?.kind === "word" ? token.text.toLowerCase() : null;
};

/**
 * The surfacing rule's inputs. The dictionary signal is the route's answer
 * where there is one; a word still waiting for its answer is taken as a
 * word for now; a word that will get none falls back to what the sight map
 * can vouch for, which is the package's own rule for a missing signal.
 */
export const surfacingContext = (session: ComposeSession): SurfacingContext => {
  const awaiting = new Set(
    [...session.awaitingDictionary].flatMap((id) => {
      const text = textOf(session, id);
      return text === null ? [] : [text];
    }),
  );
  return {
    sight: session.sight,
    thresholds: session.thresholds,
    isDictionaryWord: (text) =>
      session.dictionary.get(text) ??
      (awaiting.has(text) ||
        sightOf(session.sight, text).status !== "unobserved"),
  };
};

const knownWords = (
  session: ComposeSession,
  texts: readonly string[],
): ComposeSession => ({
  ...session,
  dictionary: new Map([
    ...session.dictionary,
    ...texts.map((t) => [t.toLowerCase(), true] as const),
  ]),
});

const awaiting = (
  session: ComposeSession,
  tokens: readonly CompositionToken[],
): ComposeSession => ({
  ...session,
  awaitingDictionary: new Set([
    ...session.awaitingDictionary,
    ...tokens.flatMap((t) => (t.kind === "word" ? [t.id] : [])),
  ]),
});

const answered = (
  session: ComposeSession,
  tokenIds: Iterable<string>,
): ComposeSession => {
  const rest = new Set(session.awaitingDictionary);
  for (const id of tokenIds) rest.delete(id);
  return { ...session, awaitingDictionary: rest };
};

/** Marks the doubt-surfaced words among `tokenIds`, if any, as one `surface` edit. */
const surfaceDoubt = (
  session: ComposeSession,
  tokenIds: ReadonlySet<string>,
  at: number,
): ComposeSession => {
  const doubtful = surfaced(
    session.composition.tokens,
    surfacingContext(session),
  )
    .filter(
      (s) => s.reason === "transcription-doubt" && tokenIds.has(s.tokenId),
    )
    .map((s) => s.tokenId);
  return doubtful.length === 0
    ? session
    : commit(session, { op: "surface", tokenIds: doubtful, at });
};

function landDictation(
  session: ComposeSession,
  action: Landed,
): ComposeSession {
  if (action.tokens.length === 0) return session;
  const before = session.composition.tokens;
  const atIndex = insertIndexFor(session);
  const inserted = commit(withClip(session, action), {
    op: "insert-dictation",
    atIndex,
    tokens: [...action.tokens],
    asr: action.asr,
    at: action.at,
  });
  if (inserted.composition === session.composition) return inserted;
  const grown =
    sentences(inserted.composition.tokens).length - sentences(before).length;
  return surfaceDoubt(
    awaiting(
      { ...inserted, cursor: session.cursor + grown, typingAfter: null },
      action.tokens,
    ),
    new Set(action.tokens.map((t) => t.id)),
    action.at,
  );
}

/** What a re-said sentence would put in place of the span; null when the span is gone. */
export function reSayPreview(
  session: ComposeSession,
  spanIds: readonly string[],
  reSaid: readonly CompositionToken[],
): SpanReplacement | null {
  const { tokens, edits } = session.composition;
  const first = spanIds[0];
  const sentenceIds =
    first === undefined ? undefined : sentenceOf(tokens, first);
  if (sentenceIds === undefined) return null;
  const sentence = sentenceIds.flatMap((id) => {
    const token = tokens[tokenIndexOf(tokens, id)];
    return token === undefined ? [] : [token];
  });
  const spanWords = sentence.filter(
    (t): t is WordToken => t.kind === "word" && spanIds.includes(t.id),
  );
  return spanReplacement(sentence, spanIds, reSaid, {
    recordingsOf: (word) => recordingsOf(edits, word),
    rejected: failedTranscriptionsOf(
      edits,
      [...new Set(spanWords.flatMap(tokenLineage))],
      spanWords.map((w) => w.text).join(" "),
      Number.POSITIVE_INFINITY,
    ),
  });
}

// A recording can land after the child confirmed, saved or took out the
// word; a gone span still goes to the package so that one is refused too.
function landReSay(
  session: ComposeSession,
  action: Landed & { readonly spanIds: readonly string[] },
): ComposeSession {
  if (action.spanIds.length === 0) return session;
  const replacement = reSayPreview(session, action.spanIds, action.tokens) ?? {
    outcome: "unaligned",
  };
  switch (replacement.outcome) {
    case "replaced": {
      const next = commit(session, {
        op: "re-say",
        tokenIds: [...replacement.spanIds],
        result: {
          outcome: "replaced",
          tokens: [...replacement.tokens],
          asr: action.asr,
        },
        at: action.at,
      });
      if (next.composition === session.composition) return next;
      const firstWord = replacement.tokens.find((t) => t.kind === "word");
      return surfaceDoubt(
        awaiting(
          pruneClips({
            ...withClip(next, action),
            focus: firstWord?.id ?? null,
          }),
          replacement.tokens,
        ),
        new Set(replacement.tokens.map((t) => t.id)),
        action.at,
      );
    }
    case "same-word":
    case "unaligned":
      return commit(session, {
        op: "re-say",
        tokenIds: [...action.spanIds],
        result: { outcome: replacement.outcome },
        at: action.at,
      });
    default:
      return assertNever(replacement, "span replacement");
  }
}

const sentenceIds = (
  tokens: readonly CompositionToken[],
  sentence: number,
): readonly string[] | undefined => sentences(tokens)[sentence];

/** The `move` edit that puts sentence `from` where sentence `to` is now. */
export function moveSentenceEdit(
  tokens: readonly CompositionToken[],
  from: number,
  to: number,
  at: number,
): CompositionEdit | null {
  const groups = sentences(tokens);
  const moving = groups[from];
  if (moving === undefined || from === to || to < 0 || to >= groups.length) {
    return null;
  }
  // `move` inserts into the tokens left after removal, so the target is the
  // length of the first `to` remaining sentences: the moved one then sits at
  // position `to` in the result.
  const toIndex = groups
    .filter((_, i) => i !== from)
    .slice(0, to)
    .reduce((n, group) => n + group.length, 0);
  return { op: "move", tokenIds: [...moving], toIndex, at };
}

const focusAfter = (session: ComposeSession): ComposeSession =>
  session.focus !== null &&
  tokenIndexOf(session.composition.tokens, session.focus) < 0
    ? { ...session, focus: null }
    : session;

export function reduceSession(
  session: ComposeSession,
  action: ComposeAction,
): ComposeSession {
  switch (action.type) {
    case "dictation":
      return landDictation(session, action);
    case "re-say":
      return focusAfter(landReSay(session, action));
    case "re-say-no-speech":
      return commit(session, {
        op: "re-say",
        tokenIds: [...action.spanIds],
        result: { outcome: "no-speech" },
        at: action.at,
      });
    case "confirm":
      return {
        ...commit(session, {
          op: "confirm",
          tokenId: action.tokenId,
          at: action.at,
        }),
        focus: null,
      };
    case "reject":
      return commit(session, {
        op: "reject",
        tokenId: action.tokenId,
        rung: action.rung,
        at: action.at,
      });
    case "choose":
      return {
        ...commit(knownWords(session, [action.to]), {
          op: "choose",
          tokenId: action.tokenId,
          from: action.from,
          to: writtenFor(session, action.tokenId, action.to),
          at: action.at,
        }),
        focus: null,
      };
    case "type":
      return {
        ...commit(knownWords(session, [action.to]), {
          op: "type",
          tokenId: action.tokenId,
          from: action.from,
          to: writtenFor(session, action.tokenId, action.to),
          at: action.at,
        }),
        focus: null,
      };
    case "save-for-adult":
      return {
        ...commit(session, {
          op: "save-for-adult",
          tokenId: action.tokenId,
          at: action.at,
        }),
        focus: null,
      };
    case "delete-sentence": {
      const ids = sentenceIds(session.composition.tokens, action.sentence);
      if (ids === undefined) return session;
      const next = commit(session, {
        op: "delete",
        tokenIds: [...ids],
        at: action.at,
      });
      return focusAfter(
        pruneClips({
          ...next,
          cursor: Math.min(
            next.cursor,
            sentences(next.composition.tokens).length,
          ),
        }),
      );
    }
    case "move-sentence": {
      const edit = moveSentenceEdit(
        session.composition.tokens,
        action.from,
        action.to,
        action.at,
      );
      return edit === null
        ? session
        : { ...commit(session, edit), typingAfter: null };
    }
    case "insert-typed": {
      const before = session.composition.tokens;
      const atIndex = insertIndexFor(session);
      const next = commit(knownWords(session, [action.text]), {
        op: "insert-typed",
        atIndex,
        tokenId: action.tokenId,
        text: writtenAt(before, atIndex, action.text),
        at: action.at,
      });
      if (next.composition === session.composition) return next;
      const grown =
        sentences(next.composition.tokens).length - sentences(before).length;
      return {
        ...next,
        cursor: session.cursor + grown,
        typingAfter: action.tokenId,
      };
    }
    case "cursor": {
      const count = sentences(session.composition.tokens).length;
      return {
        ...session,
        cursor: Math.max(0, Math.min(action.sentence, count)),
        typingAfter: null,
      };
    }
    case "focus":
      return action.tokenId !== null &&
        tokenIndexOf(session.composition.tokens, action.tokenId) < 0
        ? session
        : { ...session, focus: action.tokenId };
    case "judge": {
      if (action.judgements.length === 0) return session;
      const judged = commit(session, {
        op: "judge",
        judgements: [...action.judgements],
        at: action.at,
      });
      // Intent markers arrive after the sentence (plan § 4, point 8): the
      // doubt a judgement raises is surfaced when it lands.
      return judged.composition === session.composition
        ? judged
        : surfaceDoubt(
            judged,
            new Set(action.judgements.map((j) => j.tokenId)),
            action.at,
          );
    }
    case "describe":
      return {
        ...commit(
          action.result.outcome === "chosen"
            ? knownWords(session, [action.result.to])
            : session,
          {
            op: "describe",
            tokenId: action.tokenId,
            failedTranscriptions: [...action.failedTranscriptions],
            result:
              action.result.outcome === "chosen"
                ? {
                    ...action.result,
                    to: writtenFor(session, action.tokenId, action.result.to),
                  }
                : action.result,
            at: action.at,
          },
        ),
        focus: action.result.outcome === "chosen" ? null : session.focus,
      };
    case "surface":
      return commit(session, {
        op: "surface",
        tokenIds: [...action.tokenIds],
        at: action.at,
      });
    case "dictionary": {
      const learned = [...action.entries].flatMap(([id, isWord]) => {
        const text = textOf(session, id);
        return text === null ? [] : [[text, isWord] as const];
      });
      const next = answered(
        {
          ...session,
          dictionary: new Map([...session.dictionary, ...learned]),
        },
        action.entries.keys(),
      );
      return surfaceDoubt(next, new Set(action.entries.keys()), action.at);
    }
    case "dictionary-unknown":
      return surfaceDoubt(
        answered(session, action.tokenIds),
        new Set(action.tokenIds),
        action.at,
      );
    default:
      return assertNever(action, "compose action");
  }
}
